import { DataGridPro, GridInitialState, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { SxProps } from '@mui/material';
import { GRID_DEFAULT_LOCALE_TEXT } from '@mui/x-data-grid-pro';
import { GridCustomColDef } from '../../types';
import { CustomToolbar } from './CustomToolbar';
import { CustomFilterPanel } from './CustomFilterPanel';
import { Icon } from '../../Atoms/Icon';
import { ApiAdvancedFilter, ApiFilter, updateGridRef } from '../../../store/GeneralStore';
import {
  GridPreferences,
  PrefKey,
  resetGridPreferences,
  SaveGridResult,
  saveGridPreferences
} from '../../../store/UserCustomizations';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DEFAULT_TABLE_STYLES } from '../../../utils/styleHelpers';
import { useSnackbar } from '../../../Context/SnackbarContext';

export const customLocaleText = {
  ...GRID_DEFAULT_LOCALE_TEXT,
  filterPanelAddFilter: 'Add Filter',
  filterPanelRemoveAll: 'Remove All',
  filterValueAny: 'Any',
  filterValueTrue: 'Yes',
  filterValueFalse: 'No',
  filterOperatorBeforeOrEmpty: 'is before or empty',
  filterOperatorAfterOrEmpty: 'is after or empty',
  filterOperatorOnOrAfterOrEmpty: 'is on or after or empty',
  filterOperatorOnOrBeforeOrEmpty: 'is on or before or empty'
};

export interface UpstackDataGridProps {
  title: string;
  columns: GridCustomColDef[];
  rows: GridValidRowModel[];
  page: PrefKey;
  gridSettings: GridPreferences | undefined;
  showSearch: boolean;
  dataCy?: string;
  initialState: GridInitialState;
  apiFilter: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchFunction?: (filters: any, localLoading: boolean, forceRefresh: boolean) => void;
  filterKeys?: string[];
  quickFilterKeys?: string[];
  rowCount?: number;
  rowPrimaryKey?: string;
  advancedFiltering?: boolean;
  sxStyles?: SxProps;
  triggerSave?: boolean;
  handleNewTabModalOpen?: () => void;
  // Separate from opening the modal: the page's trigger has to clear even when the save did not happen.
  handleSaveComplete?: () => void;
  pageSizeOptions?: number[];
  showToolbar?: boolean;
  withoutSave?: boolean;
  pagination?: boolean;
  localLoading?: boolean;
  currentPage?: number;
  customNoRowsMessage?: string;
  disableColumnMenu?: boolean;
  downloadExcel?: () => void;
  hideSaveView?: boolean;
  hideExport?: boolean;
}

declare module '@mui/x-data-grid-pro' {
  interface ToolbarPropsOverrides {
    title: string;
    showSearch: boolean;
    setFilterButtonEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>>;
    saveState: () => Promise<boolean>;
    resetState: () => void;
    refreshState: () => void;
    downloadExcel?: () => void;
    hideSaveView?: boolean;
    hideExport?: boolean;
  }
}

const PAST_TENSE: Record<'save' | 'reset', string> = { save: 'saved', reset: 'reset' };

// A refused write and a failed write are different facts; the user acts differently on each.
const settingsUnavailableMessage = (
  reason: Extract<SaveGridResult, { saved: false }>['reason'],
  verb: 'save' | 'reset'
) =>
  reason === 'not-loaded'
    ? `Your table settings could not be loaded, so nothing was ${PAST_TENSE[verb]}. Reload the page and try again.`
    : `Failed to ${verb} table settings. Please try again.`;

const columnsIcon = () => {
  return <Icon type="columnsIcon" />;
};

const exportIcon = () => {
  return <Icon type="exportIcon" />;
};

const filterIcon = () => {
  return <Icon type="filterIcon" />;
};

export const UpstackDataGrid = ({
  title,
  rows,
  columns,
  initialState,
  page,
  gridSettings,
  showSearch = false,
  dataCy = '',
  apiFilter = false,
  fetchFunction,
  filterKeys,
  quickFilterKeys,
  rowCount = apiFilter ? 0 : undefined,
  rowPrimaryKey = 'id',
  advancedFiltering = false,
  sxStyles = {},
  triggerSave = false,
  handleNewTabModalOpen,
  handleSaveComplete,
  pageSizeOptions = [100, 200, 500],
  showToolbar = true,
  withoutSave = false,
  pagination = true,
  localLoading = false,
  currentPage = undefined,
  customNoRowsMessage,
  disableColumnMenu = false,
  downloadExcel,
  hideSaveView = false,
  hideExport = false
}: UpstackDataGridProps) => {
  const { setSnack } = useSnackbar();
  const apiRef = useGridApiRef();
  const [tableLoading, setTableLoading] = useState(false);
  const [stateDirty, setStateDirty] = useState(false);
  const [ready, setReady] = useState(false);

  const sideMode = apiFilter ? 'server' : 'client';

  const orderedColumns = useMemo(() => {
    let savedColumnOrder = initialState?.columns?.orderedFields;
    if (apiRef?.current?.exportState) {
      const currentState = apiRef.current.exportState();
      savedColumnOrder = currentState?.columns?.orderedFields || savedColumnOrder;
    }
    if (savedColumnOrder && savedColumnOrder.length > 0) {
      const columnMap = new Map(columns.map((col) => [col.field, col]));
      const reorderedCols: GridCustomColDef[] = [];
      const seenFields = new Set<string>();
      savedColumnOrder.forEach((field: string) => {
        const column = columnMap.get(field);
        if (column) {
          reorderedCols.push(column);
          seenFields.add(field);
        }
      });
      columns.forEach((col) => {
        if (!seenFields.has(col.field)) reorderedCols.push(col);
      });
      return reorderedCols;
    }
    return columns;
  }, [columns, initialState?.columns?.orderedFields]);

  const saveState = async (): Promise<boolean> => {
    // Reachable while resetState holds the grid unmounted: refusing silently would promise a message never sent.
    if (!apiRef?.current?.exportState) {
      setSnack({ message: settingsUnavailableMessage('failed', 'save'), type: 'error', open: true });
      return false;
    }

    const currentState = apiRef.current.exportState();
    const settingsCopy = JSON.parse(JSON.stringify(gridSettings));
    const result = await saveGridPreferences(page, { ...settingsCopy, muiConfig: currentState });

    if (result.saved) {
      setStateDirty(false);
      return true;
    }
    setSnack({ message: settingsUnavailableMessage(result.reason, 'save'), type: 'error', open: true });
    return false;
  };

  const resetState = async () => {
    setTableLoading(true);
    setReady(false);
    const result = await resetGridPreferences(page);
    if (result.saved) {
      setSnack({ message: 'Your table settings were reset successfully', type: 'success', open: true });
    } else {
      setSnack({ message: settingsUnavailableMessage(result.reason, 'reset'), type: 'error', open: true });
    }
    if (apiFilter) {
      handleTableChange();
    } else {
      setTableLoading(false);
    }
    setReady(true);
  };

  const refreshState = async () => {
    setReady(false);
    handleTableChange();
    setReady(true);
  };

  const handleTableChange = async () => {
    setStateDirty(true);

    if (apiFilter && fetchFunction) {
      setTableLoading(true);
      const currentState = apiRef?.current?.exportState();
      let filters: ApiAdvancedFilter | ApiFilter;
      if (advancedFiltering) {
        filters = (currentState?.filter?.filterModel?.items || []).reduce<ApiAdvancedFilter>(
          (filterSet, item) => {
            if (
              filterKeys?.includes(item.field) &&
              (['isEmpty', 'isNotEmpty'].includes(item.operator) ||
                (item.value !== undefined && item.value !== null && item.value !== '')) &&
              ((item.operator === 'isAnyOf' && Array.isArray(item.value) && item.value.length > 0) ||
                (item.operator !== 'isAnyOf' && !Array.isArray(item.value)))
            ) {
              filterSet.filters?.push({
                field: item.field,
                operator: item.operator,
                value: item.value
              });
            }

            return filterSet;
          },
          { filters: [] } as ApiAdvancedFilter
        );
        filters.logicalOperator = currentState?.filter?.filterModel?.logicOperator;
      } else {
        filters = (currentState?.filter?.filterModel?.items || []).reduce<ApiFilter>((filterSet, item) => {
          if (filterKeys?.includes(item.field)) filterSet[item.field] = item.value;
          return filterSet;
        }, {} as ApiFilter);
      }

      if (currentState?.filter?.filterModel?.quickFilterValues?.length && showSearch) {
        const value = currentState?.filter?.filterModel?.quickFilterValues.join(' ') as string;
        const keys = quickFilterKeys || filterKeys || [];
        filters = keys.reduce<ApiAdvancedFilter>(
          (filterSet, item) => {
            filterSet.filters?.push({
              field: item,
              operator: 'contains',
              value: value
            });

            return filterSet;
          },
          { filters: [] } as ApiAdvancedFilter
        );
        filters.logicalOperator = keys.length > 1 ? 'or' : 'and';
      }

      filters.page = (currentState?.pagination?.paginationModel?.page || 0) + 1;
      filters.pageSize = currentState?.pagination?.paginationModel?.pageSize || pageSizeOptions[0];
      const sortModel = currentState?.sorting?.sortModel;
      if (sortModel?.length) {
        filters.orders = sortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }));
      }
      await fetchFunction(filters, false, true);
      setTableLoading(false);
    }
  };

  useEffect(() => {
    updateGridRef(page, apiRef);
  }, [apiRef]);

  useEffect(() => {
    if (!triggerSave) return;

    const runSave = async () => {
      try {
        // The modal writes the whole customization document, so it must not open on a save the store refused.
        const saved = await saveState();
        if (saved && handleNewTabModalOpen) handleNewTabModalOpen();
      } catch {
        setSnack({ message: settingsUnavailableMessage('failed', 'save'), type: 'error', open: true });
      } finally {
        // In `finally` because a throw would otherwise leave the page's trigger set and the button inert.
        if (handleSaveComplete) handleSaveComplete();
      }
    };

    runSave();
  }, [triggerSave]);

  useEffect(() => {
    const confirmNavigation = (event: { preventDefault: () => void; returnValue: string }) => {
      if (!withoutSave && stateDirty) {
        event.preventDefault();
        event.returnValue = 'You have unsaved table changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', confirmNavigation);
    return () => {
      window.removeEventListener('beforeunload', confirmNavigation);
    };
  }, [stateDirty]);

  useEffect(() => {
    const exitPage = apiRef?.current?.exportState()?.pagination?.paginationModel?.page;
    if (currentPage !== undefined && exitPage !== undefined && exitPage !== currentPage - 1) {
      const pageIndex = currentPage - 1;
      apiRef?.current?.setPage(pageIndex);
    }
  }, [currentPage]);

  useEffect(() => {
    if (gridSettings && initialState) setReady(true);
  }, [gridSettings, initialState]);

  const localeText = {
    ...customLocaleText,
    noRowsLabel: customNoRowsMessage || customLocaleText.noRowsLabel
  };

  return (
    <Fragment>
      {!ready ? (
        <ComponentSpinner />
      ) : (
        <DataGridPro
          localeText={localeText}
          rows={rows}
          showCellVerticalBorder
          showColumnVerticalBorder
          paginationMode={sideMode}
          sortingMode={sideMode}
          filterMode={sideMode}
          columns={orderedColumns}
          apiRef={apiRef}
          loading={localLoading || tableLoading}
          columnHeaderHeight={DEFAULT_TABLE_STYLES.headerHeight}
          rowHeight={DEFAULT_TABLE_STYLES.rowHeight}
          sx={{ ...DEFAULT_TABLE_STYLES.sx, ...sxStyles }}
          pagination={pagination}
          hideFooter={!pagination}
          disableRowSelectionOnClick
          disableColumnMenu={disableColumnMenu}
          initialState={initialState}
          slots={{
            toolbar: CustomToolbar,
            filterPanel: CustomFilterPanel,
            columnMenuIcon: columnsIcon,
            exportIcon: exportIcon,
            openFilterButtonIcon: filterIcon
          }}
          showToolbar={showToolbar}
          slotProps={{
            root: {
              'data-testid': `${title.replace(' ', '-')}-data-grid`,
              'data-cy': dataCy
            },
            toolbar: {
              showSearch,
              title,
              saveState,
              resetState,
              refreshState,
              downloadExcel,
              hideSaveView,
              hideExport
            }
          }}
          onFilterModelChange={handleTableChange}
          onPaginationModelChange={handleTableChange}
          onSortModelChange={handleTableChange}
          onColumnOrderChange={() => setStateDirty(true)}
          onColumnVisibilityModelChange={() => setStateDirty(true)}
          pageSizeOptions={pageSizeOptions}
          rowCount={rowCount}
          getRowId={(row) => row[rowPrimaryKey]}
        />
      )}
    </Fragment>
  );
};
